import React from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { appSchema, faqSchema } from '../lib/schema';
import { PageMeta, SectionHead, PackageCard, CtaBand, FaqList, Icon, AppButton, StoreBadge, WhatsAppButton, CheckList } from '../components/ui';

// Facts here mirror the Google Play listing (com.quickway.customer). Do not add
// ratings, download counts or payment methods unless verified and kept current.
const FEATURES = [
  { icon: 'pin', title: 'Pin your car on a map', text: 'Drop a pin where the car is parked: villa driveway, tower car park or office. No need to type a long address.' },
  { icon: 'car', title: 'Save your car details', text: 'Add your car once so every booking already knows what we are washing.' },
  { icon: 'calendar', title: 'Pick a date and time', text: 'Choose the day and time that suit you, from home or from work.' },
  { icon: 'sparkle', title: 'Choose your service', text: 'From a quick exterior wash to full interior cleaning and detailing, with extras when you need them.' }
];

const CarWashApp = () => {
  const { PACKAGES, CITIES } = useSite();
  const cities = CITIES.map((c) => c.name).join(' and ');
  const faqs = [
    { q: 'Is the Quick Way app free?', a: 'Yes. The Quick Way - Mobile Car Wash app is free to download from Google Play. You only pay for the car wash you book.' },
    { q: 'Is there an iPhone app?', a: 'Not yet. The app is available on Android. iPhone users can book through the booking form on this website or on WhatsApp.' },
    { q: 'Where does the app work?', a: `Quick Way currently washes cars in ${cities}. Pin your location in the app and we will confirm coverage for your address.` },
    { q: 'How do I book a car wash in the app?', a: 'Download the app, register and add your car details, pin your location, choose a date and time, then select your service. Our team confirms your slot and final price.' },
    { q: 'Can I book without the app?', a: 'Yes. Use the booking form on this website or message us on WhatsApp. The app is simply the fastest way to order and manage your washes.' }
  ];

  return (
    <>
      <PageMeta
        title="Car Wash App Dubai & Abu Dhabi | Quick Way Android App"
        description="Order a car wash at home from your phone. The free Quick Way app lets you pin your car's location, pick a time and book a wash in Dubai and Abu Dhabi."
        path="/car-wash-app"
        crumbs={[{ name: 'Car wash app', path: '/car-wash-app' }]}
        jsonLd={[appSchema(), faqSchema(faqs)]}
      />

      <section className="hero hero--photo">
        <div className="hero__bg" aria-hidden="true">
          <picture>
            <source srcSet="/img/hero-wash.webp" type="image/webp" />
            <img src="/img/hero-wash.jpg" alt="" width="1440" height="810" fetchpriority="high" decoding="async" />
          </picture>
        </div>
        <div className="container hero__inner">
          <div className="hero__copy">
            <p className="eyebrow eyebrow--light"><Icon name="play" size={16} /> Free on Google Play</p>
            <h1>The car wash app for <span className="text-accent">{cities}</span></h1>
            <p className="hero__lead">
              Book a doorstep car wash in a few taps. Pin where your car is parked, choose a package and a time,
              and the Quick Way team comes to you at home, in your building car park or at the office.
            </p>
            <div className="btn-row">
              <AppButton label="Get it on Google Play" className="btn btn-primary btn-lg" source="app_page_hero" />
              <WhatsAppButton text="Hi Quick Way, I would like to book a car wash." label="iPhone? WhatsApp us" className="btn btn-ghost btn-lg" source="app_page_hero" />
            </div>
            <ul className="hero__facts">
              <li><Icon name="tag" size={18} /> Packages from AED {PACKAGES[0]?.price ?? 30}</li>
              <li><Icon name="pin" size={18} /> {cities}</li>
              <li><Icon name="phone" size={18} /> Android app</li>
            </ul>
          </div>
          <div className="hero__visual" aria-hidden="true">
            <div className="phone">
              <picture>
                <source srcSet="/img/app-location.webp" type="image/webp" />
                <img src="/img/app-location.jpg" alt="" width="385" height="816" fetchpriority="high" />
              </picture>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead eyebrow="Inside the app" title="Everything you need to book a car wash at home" />
          <div className="grid grid-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="feature">
                <span className="icon-tile"><Icon name={f.icon} /></span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container split split--center">
          <div>
            <p className="eyebrow">How it works</p>
            <h2>Order your wash in four steps</h2>
            <ol className="ordered-steps">
              <li><strong>Download and register.</strong> Install Quick Way from Google Play and add your car details.</li>
              <li><strong>Pin your location.</strong> Mark where the car is parked and add any building or gate details.</li>
              <li><strong>Choose date, time and service.</strong> Pick a package and any extras.</li>
              <li><strong>Get a clean car.</strong> Our team confirms your slot and final price, then washes the car where it is parked.</li>
            </ol>
            <StoreBadge source="app_page_steps" />
          </div>
          <div className="app-shot">
            <img src="/img/app-location.jpg" alt="Quick Way car wash app: pinning your car's location on a map" width="385" height="816" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead eyebrow="Packages" title="Car wash packages you can book">
            Clear package prices. For large SUVs, vans or very dirty vehicles, the final price is confirmed before the wash.
          </SectionHead>
          <div className="grid grid-3">
            {PACKAGES.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container split split--center">
          <div>
            <h2>Why book in the app instead of driving to a car wash?</h2>
            <CheckList items={[
              'No queue and no drive: the wash comes to your parked car',
              'Book from home or the office in a few taps',
              'Your car and location are saved for next time',
              `Available across ${cities}`
            ]} />
          </div>
          <div className="info-panel">
            <h3>Washing in {cities}</h3>
            <div className="related">
              {CITIES.map((c) => (
                <Link key={c.id} to={c.path} className="related__item"><span>Mobile car wash in {c.name}</span><Icon name="arrow" size={16} /></Link>
              ))}
              <Link to="/pricing" className="related__item"><span>Compare car wash prices</span><Icon name="arrow" size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container container--narrow">
          <SectionHead title="Quick Way app FAQ" />
          <FaqList items={faqs} />
          <p className="center-note">Using an iPhone? <Link to="/book">Book online</Link> or WhatsApp us — same packages, same team.</p>
        </div>
      </section>

      <CtaBand source="app_page" />
    </>
  );
};

export default CarWashApp;
